import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmAction from '@/components/confirmaction';
import { Alert, Button, Empty, Pagination, Skeleton, Switch, message } from 'antd';
import { fetchChatPreferences, updateChatPreferences, fetchChatBlocks, blockChatUser, unblockChatUser } from '@/apis/chat';
import ChatUserPicker from '@/components/chatuserpicker';
import FeaturePage from '@/components/featurepage';
import PullToRefresh from '@/components/pulltorefresh';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useMobile from '@/utils/useMobile';
import { getRequestErrorMessage } from '@/utils/requestError';
import UserIdentity from '@/components/useridentity';
import './index.scss';

const ChatSettings = () => {
  const authenticated = useSelector(state => state.user.isAuthenticated);
  const [allowed, setAllowed] = useState(true); const [items, setItems] = useState([]); const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1); const [selected, setSelected] = useState(null); const [loading, setLoading] = useState(true); const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const mobile = useMobile(); const version = useRef(0); const lock = useRef(false);
  const load = useCallback(async () => {
    if (!authenticated) return;
    const current = ++version.current; setLoading(true); setError('');
    try { const [preferences, result] = await Promise.all([fetchChatPreferences(), fetchChatBlocks({ page, pageSize: 10 })]); if (current === version.current) { setAllowed(preferences.allow_group_additions); setItems(result.data); setPagination(result.pagination); } }
    catch (err) { const text = getRequestErrorMessage(err, '聊天设置加载失败'); if (current === version.current) setError(text); throw new Error(text); }
    finally { if (current === version.current) setLoading(false); }
  }, [page, authenticated]);
  useEffect(() => { load().catch(() => {}); return () => { version.current += 1; }; }, [load]);
  const act = async operation => {
    if (lock.current) return; lock.current = true; setBusy(true);
    try { await operation(); message.success('聊天设置已更新'); await load(); }
    catch (err) { message.error(getRequestErrorMessage(err, '设置更新失败')); }
    finally { lock.current = false; setBusy(false); }
  };
  if (!authenticated) return <Navigate to="/login" replace />;
  return <FeaturePage embedded showDesktopBack={false} className="chatSettings" title="聊天设置" subtitle="管理群聊邀请和私聊屏蔽名单。">
    <PullToRefresh enabled={mobile} disabled={loading || busy} onRefresh={load} successMessage="聊天设置已刷新">
      <div className="chatSettings-panels">
        <section className="featurePage-panel" aria-labelledby="chatSettings-invitations">
          <div className="chatSettings-section-heading">
            <h2 id="chatSettings-invitations">群聊邀请</h2>
            {!mobile && <Button disabled={loading || busy} onClick={() => load().catch(() => {})}>刷新</Button>}
          </div>
          {loading ? <Skeleton active /> : error ? <Alert type="error" title={error} action={<Button onClick={() => load().catch(() => {})}>重试</Button>} /> : <div className="chatSettings-preference">
            <div className="chatSettings-preference-copy">
              <strong>允许其他用户直接添加我到普通群聊</strong>
              <p>关闭后仅影响新的普通群邀请，不会退出已有群聊，也不影响招募审批入群。</p>
            </div>
            <Switch aria-label="允许其他用户直接添加我到普通群聊" checked={allowed} disabled={busy} onChange={value => act(() => updateChatPreferences({ allow_group_additions: value }))} />
          </div>}
        </section>
        {!loading && !error && <section className="featurePage-panel chatSettings-blocks" aria-labelledby="chatSettings-blocks">
          <div className="chatSettings-section-heading">
            <h2 id="chatSettings-blocks">已屏蔽用户</h2>
            <span className="chatSettings-count">{pagination?.total ?? items.length} 位用户</span>
          </div>
          <p className="chatSettings-block-hint">屏蔽后双方不能发起或发送私聊，共同群聊不受影响。</p>
          <div className="chatSettings-block-toolbar">
            <div className="chatSettings-user-picker"><ChatUserPicker value={selected} onChange={setSelected} disabled={busy} /></div>
            <ConfirmAction title="确认屏蔽该用户？" disabled={!selected || busy} onConfirm={() => act(async () => { await blockChatUser(selected); setSelected(null); })}><Button danger disabled={!selected || busy}>屏蔽所选用户</Button></ConfirmAction>
          </div>
          {!items.length ? <div className="chatSettings-empty"><Empty description="暂无屏蔽用户" /></div> : <div className="chatSettings-block-list">
            {items.map(item => <article key={item.blocked.id} className="chatSettings-block-user">
              <UserIdentity user={item.blocked} size={40} showUid />
              <ConfirmAction title="确认解除屏蔽？" disabled={busy} onConfirm={() => act(() => unblockChatUser(item.blocked.id))}><Button disabled={busy}>解除屏蔽</Button></ConfirmAction>
            </article>)}
          </div>}
          {pagination?.total > 10 && <div className="chatSettings-pagination"><Pagination current={page} pageSize={10} total={pagination.total} onChange={setPage} showSizeChanger={false} /></div>}
        </section>}
      </div>
    </PullToRefresh>
  </FeaturePage>;
};
export default ChatSettings;
